import { useQuery } from "react-query";
import * as userService from "api/services/userService";

export const userKey = (userId) => ["users", userId];

/**
 * Store server's state of the account details
 *
 * @param {string} accountId The id of the executing user
 *
 * @returns {object} The query object return by the useQuery hook
 */
export const useAccountDetails = (accountId) =>
  useQuery(["users", "account", { userId: accountId }], () =>
    userService.getAccountDetails(accountId)
  );

/**
 * Get AMO user details.
 *
 * @param {string} userId The id of the user being fetched
 * @returns {object} The query object return by the useQuery hook
 */
export const useAmoUserDetails = (userId) =>
  useQuery(userKey(userId), () => userService.getUser(userId), {
    select: (data) => {
      const userData = data?.data;

      let specialRoleText = "";
      if (userData.amoLead) {
        specialRoleText = "Lead Reviewer";
      }
      if (userData.amoManager) {
        specialRoleText = "Manager";
      }

      return {
        id: userData.userId,
        imisId: userData.imisId,
        name: `${userData.firstName} ${userData.lastName}`,
        specialRole: specialRoleText,
        isEnabled: userData.enabled,
      };
    },
    enabled: !!userId && userId !== "new",
    staleTime: 3000,
    retry: (_, data) => data?.response?.status !== 404,
  });

/**
 * Get Communication user details.
 *
 * @param {string} userId The id of the user being fetched
 * @returns {object} The query object return by the useQuery hook
 */
export const useCommunicationUserDetails = (userId) => {
  const selectUser = (data) => {
    const userData = data?.data;
    return {
      id: userData?.userId,
      firstName: userData?.firstName ?? "",
      lastName: userData?.lastName ?? "",
      username: userData?.userName ?? "",
      title: userData?.title ?? "",
      organization: userData?.organization ?? "",
      email: userData?.email ?? "",
      phone: userData?.phoneNumber ?? "",
      phoneExtension: userData?.phoneExtension ?? "",
      isEnabled: userData?.enabled,
    };
  };

  return useQuery(userKey(userId), () => userService.getUser(userId), {
    select: selectUser,
    enabled: !!userId && userId !== "new",
    staleTime: 3000,
    retry: (_, data) => data?.response?.status !== 404,
  });
};

/**
 * Get Municipal user details.
 *
 * @param {string} userId The id of the user being fetched
 *
 * @returns {object} The query object return by the useQuery hook
 */
export const useMunicipalUserDetails = (userId) => {
  const selectMunicipalUser = (data) => {
    const userData = data?.data;
    return {
      ...data,
      data: {
        ...userData,
        firstName: userData.firstName ?? "",
        lastName: userData.lastName ?? "",
        username: userData.userName ?? "",
        title: userData.title ?? "",
        email: userData.email ?? "",
        // Remove any non-digit character from the phone
        phone: (userData.phoneNumber ?? "").replace(/[^0-9.]+/g, ""),
        phoneExtension: userData.phoneExtension ?? "",
      },
    };
  };

  return useQuery(userKey(userId), () => userService.getUser(userId), {
    select: selectMunicipalUser,
    enabled: !!userId && userId !== "new",
    staleTime: 3000,
    retry: (_, data) => data?.response?.status !== 404,
  });
};

/**
 * Store server's state of delegate status for a user
 *
 * @param {string} userIdToCheckFor               The id of the executing user
 * @param {string} municipalityIdToCheckFor       The municipality id for which to check delgate status
 * @param {boolean} isTreasurerDelegate Delegate status of the selected user
 *
 * @returns {object} The query object return by the useQuery hook
 */
export const useGetPreviousDelegate = (
  userIdToCheckFor,
  municipalityIdToCheckFor,
  isTreasurerDelegate
) => {
  const delegateCheckData = {
    municipalityIdToCheckFor,
    userIdToCheckFor,
  };

  return useQuery(
    [
      "users",
      "delegate",
      municipalityIdToCheckFor,
      { userId: userIdToCheckFor },
    ],
    () => userService.checkDelegate(delegateCheckData),
    { enabled: isTreasurerDelegate } // if treasurer delegate is turned off, no need to query
  );
};

/**
 * Store server's state of primary contact status for a user
 *
 * @param {string} userIdToCheckFor               The id of the executing user
 * @param {string} municipalityIdToCheckFor       The municipality id for which to check primary contact status
 * @param {boolean} isPrimaryContact Primary contact status of the selected user
 *
 * @returns {object} The query object return by the useQuery hook
 */
export const useGetPreviousContact = (
  userIdToCheckFor,
  municipalityIdToCheckFor,
  isPrimaryContact
) => {
  const primaryContactCheckData = {
    municipalityIdToCheckFor,
    userIdToCheckFor,
  };

  return useQuery(
    [
      "users",
      "primaryContact",
      municipalityIdToCheckFor,
      { userId: userIdToCheckFor },
    ],
    () => userService.checkPrimaryContact(primaryContactCheckData),
    { enabled: isPrimaryContact }
  );
};

/**
 * Get Treasurer user details.
 *
 * @param {string} userId The id of the user being fetched
 * @returns {object} The query object return by the useQuery hook
 */
export const useTreasurerUserDetails = (userId) =>
  useQuery(userKey(userId), () => userService.getUser(userId), {
    select: (data) => {
      const userData = data?.data;
      return {
        id: userData.userId,
        municipalityId: userData.municipalityId,
        firstName: userData.firstName ?? "",
        lastName: userData.lastName ?? "",
        title: userData.title ?? "",
        email: userData.email ?? "",
        // Remove any non-digit character from the phone
        phone: (userData.phoneNumber ?? "").replace(/[^0-9.]+/g, ""),
        phoneExtension: userData.phoneExtension ?? "",
        isEnabled: userData.enabled,
      };
    },
    enabled: !!userId && userId !== "new",
    staleTime: 3000,
    retry: (_, data) => data?.response?.status !== 404,
  });
